import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { AppColors } from '../styles/colors';
import PrimaryButton from './PrimaryButton';
import { useLang } from '../context/LangContext';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  padding-bottom: 75px; /* 모바일 하단 네비게이션 영역 고려 */

  @media (min-width: 768px) {
    /* PC: 화면 중앙에 팝업 형태로 표시 */
    align-items: center;
    justify-content: center;
    padding-bottom: 0;
  }
`;

const SheetContainer = styled.div<{ $translateY: number; $isVisible: boolean; $isDragging: boolean }>`
  width: 100%;
  background-color: ${AppColors.background};
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: column;
  transform: translateY(${({ $translateY, $isVisible }) =>
    $isVisible ? `${$translateY}px` : '100%'
  });
  transition: ${({ $isDragging }) =>
    $isDragging ? 'none' : 'transform 0.3s ease-out'
  };
  height: calc(90vh - 75px); /* 모바일에서 하단 네비게이션 영역 제외 */

  @media (min-width: 768px) {
    /* PC: 화면 꽉 채우지 않는 팝업 - 폭/높이 제한 + 모서리 둥글게 */
    width: 480px;
    max-width: calc(100vw - 32px);
    height: auto;
    max-height: 80vh;
    border-radius: 12px;
  }
`;

const HandleBar = styled.div`
  width: 71px;
  height: 4px;
  background-color: #cccccc;
  border-radius: 2px;
  margin: 12px auto;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ButtonContainer = styled.div`
  padding: 16px;
`;

interface TermsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  buttonText?: string;
  onButtonClick?: () => void;
}

export default function TermsBottomSheet({ 
  isOpen, 
  onClose, 
  children, 
  buttonText,
  onButtonClick 
}: TermsBottomSheetProps) {
  const { lang } = useLang();

  // 번역 함수
  const t = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      'confirm': { ko: '확인', en: 'Confirm' },
    };
    return translations[key]?.[lang] || key;
  };

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    onClose();
  };

  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTranslateY(0);
      // 약간의 지연 후 애니메이션 시작
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartY(e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const deltaY = e.clientY - startY;
      if (deltaY > 0) { // 아래로만 드래그 허용
        setTranslateY(deltaY);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      
      const deltaY = e.touches[0].clientY - startY;
      if (deltaY > 0) { // 아래로만 드래그 허용
        setTranslateY(deltaY);
      }
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      
      setIsDragging(false);
      
      // 30% 이상 드래그했으면 닫기
      if (translateY > window.innerHeight * 0.3) {
        onClose();
      } else {
        setTranslateY(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      setIsDragging(false);
      
      // 30% 이상 드래그했으면 닫기
      if (translateY > window.innerHeight * 0.3) {
        onClose();
      } else {
        setTranslateY(0);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, startY, translateY, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <SheetContainer 
        ref={sheetRef}
        $translateY={translateY}
        $isVisible={isVisible}
        $isDragging={isDragging}
      >
        <HandleBar
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
        
        <Content>
          {children}
        </Content>
        
        <ButtonContainer>
          <PrimaryButton onClick={handleButtonClick}>
            {buttonText || t('confirm')}
          </PrimaryButton>
        </ButtonContainer>
      </SheetContainer>
    </Overlay>
  );
}
